<template>
  <div class="offcanvas offcanvas-end" tabindex="-1" id="PatientShowOffcanvas" data-bs-backdrop="static" aria-labelledby="patientShowOffcanvasLabel">
    <div class="offcanvas-header">
      <button class="btn-back" id="btnCloseOffcanvas" type="button" name="button" data-test="btnCloseOffcanvas" data-bs-dismiss="offcanvas" :aria-label="$t('close')" @click="doClose">
        <IconArrowBack/>
        {{ $t('goBack') }}
      </button>

      <div class="">
        <p>
          <span>{{ $t('patient.currentExamPatient') }}</span>
          <b>&nbsp;{{ examDateFormatted }}</b>
        </p>

        <p>
          <span>{{ $t('patient.date') }}</span>&nbsp;<b v-if="patient">{{ patientCreatedAtFormatted }}</b>
        </p>
      </div>

      <h5 class="offcanvas-title" id="patientShowOffcanvasLabel">{{ patient.rawName }}</h5>

      <div class="action-buttons-top">
        <div class="button-row">
          <button class="btn btn-outline-secondary" type="button" name="button" data-test="btnPatientAllExams" @click="goToAllExams">
            <IconExamList/>
            {{ $t('patient.allExams') }}
          </button>

          <button class="btn btn-outline-secondary" v-if="!isSpecialist" type="button" name="button" data-test="btnAddExam" @click="openAddExamOffcanvas">
            <IconPlus/>
            {{ $t('exam.addExam') }}
          </button>

          <button class="btn btn-outline-secondary" v-if="!isSpecialist" type="button" name="button" data-test="btnPatientShare" @click="shareInformation">
            <IconShare/>
            {{ $t('externalPatient.shareSimple') }}
          </button>

          <button class="btn btn-outline-secondary" v-if="hasConsentFlag && !isSpecialist" type="button" name="button" data-test="btnExamConsent" @click="openConsentOffcanvas">
            <IconConsent/>
            {{ $t('examConsent.title') }}
          </button>

          <button class="btn btn-outline-secondary" v-if="!isSpecialist && exam" type="button" name="button" data-test="btnChangePatient" @click="openAssignOffcanvas">
            <IconChange/>
            {{ $t('patient.change') }}
          </button>
        </div>
      </div>
    </div>

    <vee-form v-slot="{ handleSubmit }" as="div" class="form-wrap" :onInvalidSubmit="onInvalidSubmit">
      <form @submit.prevent.stop="handleSubmit(doUpdate)" @keydown.enter="$event.preventDefault()">
        <div class="offcanvas-body scrollbar">
          <PatientFormData v-if="patient != null"
                          ref="patientFormData"
                          :patient="patient"
                          :rightDiopter="rightDiopter"
                          :leftDiopter="leftDiopter"
                          :underlying="underlying"
                          :ophthalmic="ophthalmic"
                          :underlyingChange="underlyingChange"
                          :ophthalmicChange="ophthalmicChange"
                          :groupedLayout="true"/>

          <button class="btn btn-outline-danger" v-if="isPowerfull" type="button" name="button" data-test="btnRemove" @click="openDeleteOffcanvas">
            <IconTrash/>
            {{ $t('patient.delete') }}
          </button>
        </div>

        <div class="offcanvas-footer" v-if="hasFormChanged">
          <button class="btn btn-outline-success" type="submit" name="button" data-test="btnSavePatient">
            {{ $t('save') }}
          </button>
        </div>
      </form>
    </vee-form>

    <teleport to="body">
      <BaseGenericOffcanvas :offcanvas_id="'offcanvasPatientInfoGeneric'" ref="offcanvasGeneric"/>
      <BasePatientAccessInfoOffcanvas ref="offcanvasPatientAccessInfo" @patient-updated="syncSavedContact"/>
      <ExamConsentOffcanvas ref="examConsentOffcanvas"/>
      <PatientExamNewOffcanvas ref="offcanvasPatientExamNew" @exam-created="onExamCreated"/>
    </teleport>
  </div>
</template>

<style lang="scss" scoped>
#PatientShowOffcanvas {
  width:                                100% !important;
  max-width:                            670px;
  padding:                              0;

  .btn {
    height:                             36px;
    min-height:                         unset;
  }

  .offcanvas-header {
    display:                            flex;
    flex-direction:                     column;
    justify-content:                    flex-start;
    align-items:                        flex-start;
    flex:                               0 0 auto;
    gap:                                15px;
    padding:                            30px 30px 15px 30px;

    .offcanvas-title {
      text-transform:                   capitalize;
    }

    p,
    .offcanvas-title {
      color:                            var(--Brand-Primary);
      margin:                           0;
    }

    p:first-child,
    p span,
    .offcanvas-title {
      font-weight:                      300;
    }

    p {
      font-size:                        var(--font-size-14);
      line-height:                      140%; /* 19.6px */

      b {
        font-weight:                    600;
      }
    }

    > div {
      display:                          flex;
      gap:                              15px;
      width:                            100%;
    }

    .action-buttons-top {
      display:                          flex;
      flex-direction:                   column;
      gap:                              10px;

      .button-row {
        display:                        flex;
        flex-direction:                 row;
        flex-wrap:                      wrap;
        align-items:                    center;
        gap:                            10px;

        &:last-child:not(:first-child) {
          padding-top:                  10px;
          border-top:                   1px dashed var(--Shades-30-Border);
        }

        .btn {
          display:                      flex;
          align-items:                  center;
          gap:                          10px;
        }
      }
    }
  }

  .form-wrap {
    display:                            flex;
    flex-direction:                     column;
    flex:                               1;
    min-height:                         0;
  }

  form {
    display:                            flex;
    flex-direction:                     column;
    flex:                               1;
    min-height:                         0;
  }

  .offcanvas-body {
    display:                            flex;
    flex-direction:                     column;
    flex:                               1;
    min-height:                         0;
    gap:                                20px;
    margin-right:                       30px;
    padding:                            15px 10px 20px 30px;
    overflow-x:                         hidden;
  }

  .btn-outline-danger,
  .btn-outline-success {
    font-size:                          var(--font-size-12);
    font-weight:                        500;
    line-height:                        normal;
    letter-spacing:                     3.12px;
    text-transform:                     uppercase;
    display:                            flex;
    justify-content:                    center;
    align-items:                        center;
    gap:                                10px;
    align-self:                         stretch;
    height:                             36px;
    min-height:                         unset;
    padding:                            10px 15px 10px 10px;
  }

  .offcanvas-footer {
    @include                            border_radius(10px 10px 0 0);
    @include                            box_shadow(0 0 10px 0 rgba(0, 0, 0, 0.25));
    position:                           relative;
    display:                            flex;
    flex-direction:                     row;
    gap:                                15px;
    padding:                            20px;
    z-index:                            4;
    isolation:                          isolate;

    .btn-outline-success {
      color:                            var(--Brand-Secondary);
      width:                            100%;
      border:                           1.5px solid var(--Brand-Secondary);
      background:                       #D3FDEF;
    }
  }
}
</style>

<script>
import { useUserStore } from '@/store/user'
import { useExamFilterStore } from '@/store/examFilter'
import ExamConsentOffcanvas from '../../exam/exam-show/ExamConsentOffcanvas.vue'
import PatientFormData from '../PatientFormData.vue'
import PatientExamNewOffcanvas from '../patient-show/PatientExamNewOffcanvas.vue'

export default {
  name: 'PatientShowOffcanvas',
  components: {
    ExamConsentOffcanvas,
    PatientFormData,
    PatientExamNewOffcanvas
  },
  data: () => ({
    loading: false,
    alert: {
      message: null,
      class: null,
    },
    session: {
      collaborator: {},
      currentClinic: {}
    },
    isSpecialist: null,
    isPowerfull: null,
    patient: {
      id: null,
      firstName: '',
      lastName: '',
      mrn: '',
      birthday: '',
      birthdayAux: '',
      document0: '',
      document1: '',
      document2: '',
      document3: '',
      mothersName: '',
      gender: 'M',
      weight: '',
      height: '',
      telephone1: '',
      telephone2: '',
      email: '',
      address: '',
      addressNumber: '',
      anamnesis: {
        otherDisease: ''
      },
      district: '',
      postalCode: '',
      city: '',
      state: '',
      country: '',
      other: '',
    },
    rightDiopter: {
      spherical: '',
      cylindrical: '',
      axis: '',
    },
    leftDiopter: {
      spherical: '',
      cylindrical: '',
      axis: '',
    },
    underlying: {
      diabetes: false,
      hypertension: false,
      cholesterol: false,
      smoker: false,
      nwn_underlying: false,
    },
    ophthalmic: {
      diabeticRetinopathy: false,
      dmri: false,
      glaucoma: false,
      cataract: false,
      pterygium: false,
      lowVisualAcuity: false,
      nwn_ophthalmic: false,
    },
    patientId: null,
    patientName: null,
    exam: null,
    mandatoryDocument: null,
    loadedPatient: null,
    hasFormChanged: false,
    initialForm: {},
    changedFormItems: [],
    underlyingChange: false,
    ophthalmicChange: false,
    offcanvasPatientShow: null,
    resolvePromise: null,
    rejectPromise: null,
    hasConsentFlag: false,
  }),
  watch: {
    patientId: {
      immediate: true,
      handler (patientId) {
        if (patientId) {
          this.$nextTick(() => {
            this.hasFormChanged = false
            this.changedFormItems = []
          })
        }
    }, deep: true },
    patient: {
      immediate: true,
      handler(newValue) {
        this.checkFormChange(newValue, 'patient')
    }, deep: true},
    rightDiopter: {
      immediate: true,
      handler(newRightDiopter) {
        this.checkFormChange(newRightDiopter, 'rightDiopter')
    }, deep: true},
    leftDiopter: {
      immediate: true,
      handler(newLeftDiopter) {
        this.checkFormChange(newLeftDiopter, 'leftDiopter')
    }, deep: true},
    underlying: {
      immediate: true,
      handler(newUnderlying) {
        this.underlyingChange = false

        this.checkFormChange(newUnderlying, 'underlying')

        this.$nextTick(() => {
          this.underlyingChange = true
        })
    }, deep: true},
    ophthalmic: {
      immediate: true,
      handler(newOphthalmic) {
        this.ophthalmicChange = false

        this.checkFormChange(newOphthalmic, 'ophthalmic')

        this.$nextTick(() => {
          this.ophthalmicChange = true
        })
    }, deep: true},
  },
  computed: {
    patientCreatedAtFormatted() {
      return this.$utils.formatDate(this, this.patient?.createdAt)
    },
    examDateFormatted() {
      const date = this.exam?.date || this.patient?.lastExamDate
      return this.$utils.formatDate(this, date) || '----'
    },
    showSecondButtonRow() {
      return (this.hasConsentFlag && !this.isSpecialist) || (!this.isSpecialist && !!this.exam)
    },
  },
  mounted() {
    const userStore = useUserStore()
    this.session = userStore.sessionInfo

    this.isSpecialist = this.session.isSpecialist
    this.isPowerfull = this.session.isPowerfull
    this.hasConsentFlag = this.session.features.includes('consentFlag')

    this.mandatoryDocument = this.session.currentClinic.mandatoryDocument
  },
  methods: {
    show(options = {}) {
      // this.patient = null
      this.patientId = null
      this.loadedPatient = null

      if (options.patientId)
        this.patientId = options.patientId

      this.exam = options.exam || null

      this.fetchData()

      this.offcanvasPatientShow = bootstrap.Offcanvas.getOrCreateInstance(document.getElementById('PatientShowOffcanvas'));
      this.offcanvasPatientShow.show();

      return new Promise((resolve, reject) => {
        this.resolvePromise = resolve
        this.rejectPromise = reject
      })
    },

    openAssignOffcanvas() {
      if (this.isSpecialist || !this.exam)
        return

      this.$emit('change-patient', {
        exam: this.exam,
        reassign: true,
        patientFullName: this.patient?.rawName,
      })
    },

    async refreshAfterReassign(updatedExam) {
      if (!this.exam || !updatedExam || this.exam.id !== updatedExam.id)
        return

      this.exam = updatedExam

      try {
        let response = await this.$examService.get({
          id: this.exam.id,
          researchFlag: this.session.features.includes('researchFlag')
        })

        this.patientId = response.patient.id
        this.fetchData()
      } catch (err) {
        this.$message.popup(this, 'request_error', 'operationFail', 'error')
      }
    },

    goToAllExams() {
      if (!this.patient?.id)
        return

      if (this.offcanvasPatientShow)
        this.offcanvasPatientShow.hide()

      const examFilterStore = useExamFilterStore()

      examFilterStore.refreshFilter({
        newRequest: true,
        filter: {
          ...examFilterStore.filter.filter,
          patientID: this.patient.id,
          patientFullName: this.patient.rawName
        }
      })

      sessionStorage.setItem('examFilters', JSON.stringify(examFilterStore.filter))
      this.$router.push('/exam/')
    },

    // doConfirm() {
    //   this.offcanvasPatientShow = null;
    //   this.resolvePromise(true)
    // },

    doClose() {
      this.offcanvasPatientShow = null;
      this.resolvePromise(false)
    },

    checkFormChange(value, obj) {
      let hasObj = this.changedFormItems.includes(obj)
      if (JSON.stringify(value) === JSON.stringify(this.initialForm[obj])) {
        if (hasObj) {
          let index = this.changedFormItems.indexOf(obj)
          this.changedFormItems.splice(index, 1)
        }

        if (this.changedFormItems.length > 0)
          return

        this.hasFormChanged = false
      } else {
        if (!hasObj)
          this.changedFormItems.push(obj)

        this.hasFormChanged = true
      }
    },

    async fetchData() {
      this.alert.message = null
      this.initialForm = {}

      if (!this.session.features) {
        const userStore = useUserStore()
        this.session = userStore.sessionInfo
      }

      let params = {
        id: this.patientId,
        researchFlag: this.session.features.includes('researchFlag')
      }

      this.loading = true
      NProgress.start()
      try {
        let response = await this.$patientService.getPatient(params)

        const patientMasks = {
          document2: 'inputMask.document2',
        }
        const formattedPatient = this.$utils.applyFieldMasks(this.$i18n, response.patient, patientMasks)

        this.patient = formattedPatient
        this.loadedPatient = formattedPatient

        const DATE_FORMAT  = this.$i18n.t('dateFormat')
        this.patient.birthdayAux = this.patient.birthday ? moment.utc(this.patient.birthday).format(DATE_FORMAT) : ""

        if (!_.isEmpty(response.diopter.right))
          this.rightDiopter = response.diopter.right

        if (!_.isEmpty(response.diopter.left))
          this.leftDiopter = response.diopter.left

        let anamnesis = response.patient.anamnesis ? response.patient.anamnesis : {}

        for (let key of Object.keys(this.ophthalmic)) {
          if (key in anamnesis)
            this.ophthalmic[key] = anamnesis[key]
        }

        for (let key of Object.keys(this.underlying)) {
          if (key in anamnesis)
            this.underlying[key] = anamnesis[key]
        }

        if (response.patient.anamnesis?.otherDisease) {
          this.patient.anamnesis.otherDisease = response.patient.anamnesis.otherDisease;
        }

        const formWrap = {
          patient: this.patient,
          rightDiopter: this.rightDiopter,
          leftDiopter: this.leftDiopter,
          underlying: this.underlying,
          ophthalmic: this.ophthalmic
        }

        this.initialForm = _.cloneDeep(formWrap)

        this.patientName = `${this.patient.firstName} ${this.patient.lastName}`
      } catch (err) {
        this.$message.popup(this, 'request_error', 'operationFail', 'error')
        // this.alert = this.$message.alert(this, 'request_error', 'danger')
      }
      NProgress.done(true)
      this.loading = false
    },

    onInvalidSubmit({ errors }) {
      this.$refs.patientFormData?.revealFirstInvalidField(Object.keys(errors))
    },

    async doUpdate() {
      this.alert.message = null;

      try {
        const mandatoryDocumentWasSet = !_.isEmpty(this.mandatoryDocument) && this.mandatoryDocument != 'none'
        if (mandatoryDocumentWasSet) {
          if (_.isEmpty(this.patient[this.mandatoryDocument])) {
            this.$message.popup(this, 'patient.mandatoryDocumentError', 'operationWarning', 'warn')
            this.$refs.patientFormData?.revealFirstInvalidField([this.mandatoryDocument])
            return
          }
        }

        this.patient.birthday = this.patient.birthdayAux

        let anamnesis = {}

        if (this.patient.anamnesis?.otherDisease) {
          anamnesis.otherDisease = this.patient.anamnesis.otherDisease
        }

        Object.assign(anamnesis, this.underlying)
        Object.assign(anamnesis, this.ophthalmic)

        let params = {
          patient: this.patient,
          anamnesis,
          rightDiopter: this.rightDiopter,
          leftDiopter: this.leftDiopter,
        }

        this.loading = true
        NProgress.start()
        await this.$patientService.updatePatient(params)
        this.$message.popup(this, 'general.updatedSuccess', 'operationSuccess', 'success')
        this.fetchData()
        this.emitter.emit('patient-updated')
      } catch(err) {
        if (err.response?.data?.errorMessage === 'invalidPatient') {
          this.$message.popup(this, 'patient.invalidFieldsError', 'operationWarning', 'warn')
        } else {
          this.$message.popup(this, 'general.createdError', 'operationFail', 'error')
        }
      }
      NProgress.done(true)
      this.loading = false
    },

    async openDeleteOffcanvas() {
      const ok = await this.$refs.offcanvasGeneric.show({
        title: this.$i18n.t('confirm'),
        text: this.$i18n.t('patient.removalConfirmation'),
        okButton: this.$i18n.t('remove'),
        okButtonClass: 'btn-danger',
        cancelButton: this.$i18n.t('cancel'),
        cancelButtonClass: 'btn-outline-secondary'
      })
      if (ok) {
        this.doDelete()
      }
    },

    async doDelete() {
      this.alert.message = null

      let params = {
        id: this.patientId
      }
      this.loading = true
      NProgress.start()
      try {
        await this.$patientService.deletePatient(params)
        this.$message.popup(this, 'general.removedSuccess', 'operationSuccess', 'success')

        if (this.offcanvasPatientShow)
          this.offcanvasPatientShow.hide()

        this.$router.push({name: 'patient-list', params: {patientID: this.patientId}})
      } catch (err) {
        this.$message.popup(this, 'general.removedError', 'operationFail', 'error')
        // this.alert = this.$message.alert(this, 'general.removedError', 'danger')
      }
      NProgress.done(true)
      this.loading = false
    },

    toPatientProgression() {
      this.$router.push({name: 'patient-progression', params: {patientID: this.patientId}})
    },

    async shareInformation() {
      const ok = await this.$refs.offcanvasPatientAccessInfo.show({
        patient: this.patient
      })
      if (ok) {
        NProgress.done(true)
      }
    },

    // contact saved directly from the share offcanvas: update the baseline so it isn't seen as a pending change
    syncSavedContact(updatedPatient) {
      if (!this.initialForm.patient)
        return

      this.initialForm.patient.email = updatedPatient.email
      this.initialForm.patient.telephone2 = updatedPatient.telephone2
      this.checkFormChange(this.patient, 'patient')
    },

    async openConsentOffcanvas() {
      const assigned = await this.$refs.examConsentOffcanvas.show({
        autoCreateExam: true,
        patientId: this.patient.id,
      });

      if (assigned) {
        this.goToAllExams()
      }
    },

    openAddExamOffcanvas() {
      this.$refs.offcanvasPatientExamNew.show({
        patientId: this.patient.id,
        patientName: this.patient.rawName,
      })
    },

    onExamCreated() {
      if (!this.exam)
        return

      this.goToAllExams()
    },
  }
}
</script>
